import {gql} from '@apollo/client';

import {SchemaTag} from 'folder-items/FileConstants';
import {doQuerySafe, readFragmentFromCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ResultsRowMetadata} from 'notebook/integrations/results/ResultsTableConstants';
import {convertTagToTypedValue} from 'notebook/tables/typed-values/DataTypes';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {ColumnGroup} from '../columns/ColumnDefinitions';
import {LiveCellValue} from '../LiveCellValues';
import {
  SampleLookupInfo,
  StructuredTableColumn,
  StructuredTableRow,
  StructuredTableRowUpdater,
} from '../StructuredTableConstants';
import {
  BulkResultLiveDataQuery,
  BulkResultLiveDataQueryVariables,
} from './__generated/BulkResultLiveDataQuery';
import {buildStandardUpdatedCells} from './updateRowUtils';

type ResultInfo = ExtractByTypename<BulkResultLiveDataQuery['results'][number], 'ResultNode'>;

export const BULK_RESULT_LIVE_DATA_QUERY = gql`
  query BulkResultLiveDataQuery($resultIds: [StrictString!]!) @skipAudit @engComponent(name: RESULTS) {
    results(ids: $resultIds) {
      ...ResultGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('Result').fragment}
`;

async function _bulkFetchModel(
  sampleLookupInfos: Array<SampleLookupInfo>
): Promise<BulkResultLiveDataQuery['results']> {
  const {results} = await doQuerySafe<BulkResultLiveDataQuery, BulkResultLiveDataQueryVariables>(
    {query: BULK_RESULT_LIVE_DATA_QUERY},
    {
      variables: {
        resultIds: sampleLookupInfos.map((info) => info.idForLookup),
      },
      fetchPolicy: 'network-only',
    }
  );
  return results;
}

export const ResultsTableRowUpdater: StructuredTableRowUpdater<ResultInfo, ResultsRowMetadata> = {
  fetchModel(sampleLookupInfo: SampleLookupInfo): Promise<ResultInfo | InaccessibleInfoGraphQL> {
    return _bulkFetchModel([sampleLookupInfo]).then((results) => results[0]);
  },

  bulkFetchModel(sampleLookupInfos: Array<SampleLookupInfo>): Promise<BulkResultLiveDataQuery['results']> {
    return _bulkFetchModel(sampleLookupInfos);
  },

  readFromCache({idForLookup: resultId}: SampleLookupInfo): ResultInfo | null {
    return readFragmentFromCache(getFragmentWrapper('Result'), resultId);
  },

  updateCell: (
    model: ResultInfo | InaccessibleInfoGraphQL,
    columnGroup: ColumnGroup | null,
    column: StructuredTableColumn
  ): LiveCellValue => {
    // Only update if the result is accessible and unarchived.
    if (isInaccessible(model) || model.archiveRecord != null) {
      return 'NO_VALUE';
    }
    const matchingTag = model.tagsJson.find(
      (tag: SchemaTag) => tag.tagSchemaField.apiId === column.schemaFieldApiId
    );
    return matchingTag != null ? convertTagToTypedValue(matchingTag) : 'NO_VALUE';
  },

  updateRow(
    model: ResultInfo | InaccessibleInfoGraphQL,
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<ResultsRowMetadata>,
    columnGroup: ColumnGroup | null
  ): StructuredTableRow<ResultsRowMetadata> {
    if (isInaccessible(model)) {
      return row;
    }

    if (model.archiveRecord != null) {
      // Early return row metadata without updating cells for archived results.
      return {...row, lastSyncedArchivePurpose: model.archiveRecord.purpose};
    }

    return {
      ...row,
      lastSyncedArchivePurpose: null,
      cells: buildStandardUpdatedCells(model, columns, row, columnGroup, this.updateCell),
    };
  },
};
